import type { SuccessFeeTermsDocument, SuccessFeeTermsState } from '@fluenceur/types';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Check } from 'iconoir-react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SuccessFeeTermsDialog } from './SuccessFeeTermsDialog';

interface LockedTermsStepProps {
  state: SuccessFeeTermsState;
  document: SuccessFeeTermsDocument | null;
}

/**
 * The first way out: reading the terms, and accepting them for the organization.
 *
 * Once the version in force is the accepted one this step is done, and says so,
 * because the lock only lifts when the Shopify approval lands as well.
 */
export const LockedTermsStep: React.FC<LockedTermsStepProps> = ({ state, document }) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isAccepted =
    state.termsAccepted ?? (!!state.versionInForce && state.acceptedVersion === state.versionInForce);
  const isAcceptanceOffered = state.canAccept && !isAccepted;

  return (
    <VStack align="flex-start" gap={3}>
      {isAccepted ? (
        <HStack gap={2} color="fg.muted">
          <Check width={16} height={16} />
          <Text>{t('legal.successFeeTerms.locked.termsDone')}</Text>
        </HStack>
      ) : (
        <Text color="fg.muted">{t('legal.successFeeTerms.locked.termsBody')}</Text>
      )}

      {!isAcceptanceOffered && !isAccepted && (
        <Text color="fg.muted" fontSize="sm">
          {t('legal.successFeeTerms.viewerHelper')}
        </Text>
      )}

      <Button onClick={() => setIsDialogOpen(true)} variant="outline" size="sm">
        {isAcceptanceOffered
          ? t('legal.successFeeTerms.locked.termsAction')
          : t('legal.successFeeTerms.locked.termsReadAction')}
      </Button>

      <SuccessFeeTermsDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        document={document}
        canAccept={isAcceptanceOffered}
      />
    </VStack>
  );
};
